import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Stack, alpha } from "@mui/material";
import {
  LocalPharmacy as LocalPharmacyIcon,
} from "@mui/icons-material";

import SelectableListRow from "../../molecules/SelectableListRow";
import PrescriptionEditor from "./PrescriptionEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  ListGroupLabel,
  ReadOnlyStrip,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import { useListTreatmentsByPatientMutation } from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const TAB_KEY = "prescriptions";

/**
 * PrescriptionsTab — two-bucket master-detail for prescriptions.
 *
 * Left panel: treatments split into "Reçete Talebi" (prescriptionRequested,
 * no meds yet) and "Reçete Listesi" (has medications). Right panel:
 * PrescriptionEditor backed by useInlineAutosave. Selection is restored from
 * localStorage in session mode.
 *
 * Reçeteler tab does NOT create new treatments — empty-state copy points
 * users to Tedaviler.
 *
 * @property {"standalone" | "session"} mode
 * @property {string} patientId
 * @property {string} token
 * @property {boolean} [canEdit=true]
 * @property {object} [initialData]  Session: { treatments, medicineTemplates, examinationId }
 * @property {object} [activeTreatment]  Reserved — future shortcut
 * @property {() => void} [onRecordChanged]
 * @property {(promise: Promise) => Promise} [registerAutoSave]
 * @property {(flush: () => Promise) => void} [registerFlush]
 * @property {(status: "idle"|"saving"|"saved"|"error") => void} [onSaveStatusChange]
 */
export default function PrescriptionsTab({
  mode,
  patient,
  patientId,
  token,
  canEdit = true,
  initialData,
  activeTreatment: activeTreatmentProp,
  onRecordChanged,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onGoToTreatments,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // ── Local (section-internal) alert ─────────────────────────────
  const [localAlert, setLocalAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback(
    (message, severity = "success", duration = 3500) => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
      setLocalAlert({ open: true, severity, message });
      alertTimerRef.current = setTimeout(() => {
        setLocalAlert((a) => ({ ...a, open: false }));
        alertTimerRef.current = null;
      }, duration);
    },
    []
  );
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(
    () => () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    },
    []
  );
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback(
    (m) => showLocal(m, "error", 5000),
    [showLocal]
  );

  // ── Mutations ───────────────────────────────────────────────────
  const [listTreatmentsByPatient, { isLoading: isLoadingTreatments }] =
    useListTreatmentsByPatientMutation();

  // ── Data state ──────────────────────────────────────────────────
  const [treatments, setTreatments] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // ── UI state ────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState(null);

  const examinationId = initialData?.examinationId;

  // ── Initial hydrate ─────────────────────────────────────────────
  // Session mode: parent ExaminationSession payload carries treatments+doctors.
  useEffect(() => {
    if (mode === "session") {
      setTreatments(
        Array.isArray(initialData?.treatments) ? initialData.treatments : []
      );
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.treatments]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      // İlaç seçenekleri editörün drawer'ında sunucudan sayfalı aranır
      const treatmentsRes = await listTreatmentsByPatient({ token, patientId }).unwrap();
      setTreatments(treatmentsRes?.data?.treatments || []);
    } catch (_) {
      showError("Reçeteler yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [
    mode,
    token,
    patientId,
    listTreatmentsByPatient,
    showError,
  ]);

  useEffect(() => {
    if (mode === "standalone") {
      refresh();
    }
  }, [mode, refresh]);

  const needsPrescription = useMemo(
    () =>
      (treatments || []).filter(
        (t) =>
          Boolean(t?.prescriptionRequested) &&
          (!t?.medications || t.medications.length === 0)
      ),
    [treatments]
  );

  const withPrescription = useMemo(
    () =>
      (treatments || []).filter(
        (t) => Array.isArray(t?.medications) && t.medications.length > 0
      ),
    [treatments]
  );

  // ── allTreatments + restore/persist selection ───────────────────
  const allTreatments = useMemo(
    () => [...needsPrescription, ...withPrescription],
    [needsPrescription, withPrescription]
  );

  // Açılışta: session'da önce localStorage'daki açık kaydı geri yükle; yoksa
  // AKTİF tedaviye ait reçete varsa onu seç. Aktif yoksa session'da ilk kaydı
  // seç (boş ekran olmasın), standalone'da seçim yapma.
  useEffect(() => {
    if (!initialLoaded || allTreatments.length === 0) return;

    if (mode === "session" && examinationId) {
      const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
      if (restored && allTreatments.some((r) => r._id === restored)) {
        setSelectedId(restored);
        return;
      }
    }

    const activeRec = allTreatments.find((r) => r.isActive);
    if (activeRec) {
      setSelectedId(activeRec._id);
    } else if (mode === "session") {
      setSelectedId(allTreatments[0]?._id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, initialLoaded]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  const selected = useMemo(
    () => allTreatments.find((r) => r._id === selectedId) || null,
    [allTreatments, selectedId]
  );
  // Sadece aktif tedavinin reçetesi düzenlenebilir; tamamlanan/diğerleri
  // tıklanıp görüntülenir ama düzenlenemez.
  const selectedEditable = Boolean(
    canEdit && selected && String(selected.status || "").toLowerCase() === "active"
  );

  // ── Editor callbacks ────────────────────────────────────────────
  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setTreatments((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    showSuccess("Reçete güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(
          serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`
        );
        return;
      }
      showError(serverMsg || "Reçete kaydedilemedi.");
    },
    [showError, terms]
  );

  // ── Render ──────────────────────────────────────────────────────
  const renderRow = (tr, variant) => {
    const meds = Array.isArray(tr.medications) ? tr.medications : [];
    const diagnosesLabel =
      (tr.diagnoses || [])
        .map(
          (d) =>
            d.diagnosisName ||
            (d.diagnosisDisease && d.diagnosisDisease.name) ||
            ""
        )
        .filter(Boolean)
        .join(", ") || "Reçete";
    const dateStr = (() => {
      try {
        return new Date(tr.createdInfo || tr.createdAt).toLocaleDateString(
          "tr-TR",
          { day: "2-digit", month: "short", year: "numeric" }
        );
      } catch (_) {
        return "—";
      }
    })();
    const chipLabel = variant === "needs" ? "Reçete yazılacak" : `${meds.length} ilaç`;
    const chipBg =
      variant === "needs"
        ? alpha(COLORS.warning, 0.12)
        : alpha(palette.primary, 0.08);
    const chipFg =
      variant === "needs" ? COLORS.warningDarker : palette.primary;
    const active = tr._id === selectedId;

    return (
      <SelectableListRow
        key={tr._id}
        active={active}
        onClick={() => setSelectedId(tr._id)}
        sx={LIST_ROW_SX}
        meta={dateStr}
        title={diagnosesLabel}
        trailing={
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              borderRadius: 999,
              bgcolor: chipBg,
              color: chipFg,
              fontWeight: 600,
            }}
          />
        }
      />
    );
  };

  const showLoading =
    mode === "standalone" && !initialLoaded && isLoadingTreatments;

  // activeTreatmentProp is reserved for a future "aktif tedavi shortcut"
  void activeTreatmentProp;

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Reçeteler"
        count={allTreatments.length}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : allTreatments.length === 0 ? (
          // Reçete, tedavi planına ilaç eklenerek yazılır; bu sekme oluşturmaz
          <WorkspaceEmpty
            icon={<LocalPharmacyIcon />}
            title="Henüz reçete yok"
            lead="Reçete, tedavi planına ilaç eklenerek yazılır. Önce"
            linkLabel="tedavi planı oluşturun"
            readOnlyText={`Bu ${terms.patientLower} için yazılmış reçete veya reçete talebi bulunmuyor.`}
            onCreate={canEdit && onGoToTreatments ? onGoToTreatments : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <>
                {needsPrescription.length > 0 && (
                  <>
                    <ListGroupLabel first count={needsPrescription.length}>
                      Reçete Talebi
                    </ListGroupLabel>
                    <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                      {needsPrescription.map((tr) => renderRow(tr, "needs"))}
                    </Stack>
                  </>
                )}
                {withPrescription.length > 0 && (
                  <>
                    <ListGroupLabel first={needsPrescription.length === 0} count={withPrescription.length}>
                      Reçete Listesi
                    </ListGroupLabel>
                    <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                      {withPrescription.map((tr) => renderRow(tr, "has"))}
                    </Stack>
                  </>
                )}
              </>
            }
            editor={
              !selected ? (
                <SelectPrompt
                  icon={<LocalPharmacyIcon />}
                  title="Bir reçete seçin"
                  description="Reçeteyi görmek ve düzenlemek için soldaki listeden bir kayıt seçin."
                />
              ) : (
                <>
                  {!selectedEditable && (
                    <ReadOnlyStrip>Tamamlanmış reçete — yalnızca görüntüleme</ReadOnlyStrip>
                  )}
                  <PrescriptionEditor
                    key={selected?._id}
                    record={selected}
                    mode={mode}
                    patient={patient}
                    patientId={patientId}
                    token={token}
                    examinationId={examinationId}
                    canEdit={selectedEditable}
                    onSaved={handleSaved}
                    onError={handleEditorError}
                    registerAutoSave={registerAutoSave}
                    registerFlush={registerFlush}
                    onSaveStatusChange={onSaveStatusChange}
                  />
                </>
              )
            }
          />
        )}
      </WorkspaceBody>
    </Box>
  );
}
